import React, {useCallback} from 'react';
import {useNavigate, useParams} from 'react-router-dom';


import {WidthItem} from 'src/components/width-item';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';

import {CreateUserStep} from '../../components/create-user-step/create-user-step';
import {CREATE_USER_STEPS} from '../../constants';

import {UserDetailsController} from './user-details.controller';

type Props = {
    userId?: number,
    onUserCreate: (createdUserId: number) => void,
    onUserDetailsSet: () => void,
    canSetupAdminRole: boolean,
    canViewLimits: boolean,
};

export const CreateUserDetailsStep = (props: Props) => {
    const {userId, onUserCreate, onUserDetailsSet, canSetupAdminRole, canViewLimits} = props;
    const queryParams = useParams<{accountId: string}>();
    const accountId = queryParams.accountId ? +queryParams.accountId : -1;
    const navigate = useNavigate();

    const {userCreateSubmit, userFieldChange} = useEventTracking();

    const onCancel = useCallback(() => {
        userCreateSubmit({place: 'User Details', action: 'Cancel'});
        navigate(-1);
    }, [navigate, userCreateSubmit]);

    const onFieldChange = (section: string) => (parameterName: string) => {
        userFieldChange({
            parameterName,
            section,
            viewType: 'New',
        });
    };

    const proceedUser = useCallback((existedUserId?: number) => {
        if (existedUserId && typeof existedUserId === 'number') {
            navigate(`/profile/user/${existedUserId}`);
        } else {
            onUserDetailsSet();
        }
    }, [onUserDetailsSet, navigate]);

    return (
        <ValidationContextProvider>
            <UserDetailsController {...{
                userId,
                accountId,
                onProceed: proceedUser,
                onUserCreate,
                onFieldChange,
            }}>
                {({content, busy, onSave, loadingError}) => (
                    <CreateUserStep
                        selectedStep={CREATE_USER_STEPS.USER_DETAILS}
                        loadingError={loadingError}
                        canViewLimits={canViewLimits}
                        canSetupAdminRole={canSetupAdminRole}
                        buttonsBlock={[
                            <Button key="Cancel" variant="Type.SECONDARY" onClick={onCancel}>Cancel</Button>,
                            <Button key="Save" variant="Type.PRIMARY" busy={busy} onClick={() => onSave()}>
                                {userId ? 'Save & Continue' : 'Create'}
                            </Button>,
                        ]}
                        content={(
                            <WidthItem width={1000}>
                                {content}
                            </WidthItem>
                        )}
                    />
                )}
            </UserDetailsController>
        </ValidationContextProvider>
    );
};
